import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../services/api/client.js';

const DashboardDataContext = createContext(null);
const RESOURCE_TTLS = {
  publishingPrefs: 2 * 60 * 1000,
  entitlements: 2 * 60 * 1000,
  schedule: 30 * 1000,
  socialAccounts: 2 * 60 * 1000,
};

const RESOURCE_KEYS = Object.keys(RESOURCE_TTLS);

export function DashboardDataProvider({ children }) {
  const [state, setState] = useState(() => ({
    publishingPrefs: null,
    entitlements: null,
    schedule: [],
    socialAccounts: [],
    fetchedAt: emptyFetchedAt(),
    refreshing: false,
    error: null,
  }));
  const stateRef = useRef(state);
  const inFlightRef = useRef({});

  stateRef.current = state;

  async function ensureDashboardData({ force = false } = {}) {
    const keys = staleResourceKeys(stateRef.current, force);
    if (!keys.length) return stateRef.current;

    setState(previous => ({ ...previous, refreshing: true, error: null }));
    const results = await Promise.allSettled(keys.map(key => loadResource(key)));
    const now = Date.now();
    setState(previous => {
      const next = { ...previous, fetchedAt: { ...previous.fetchedAt }, refreshing: hasInFlightRequests(), error: null };
      results.forEach((result, index) => {
        const key = keys[index];
        if (result.status === 'fulfilled') {
          next[key] = result.value;
          next.fetchedAt[key] = now;
        } else if (!next.error) {
          next.fetchedAt[key] = now;
          next.error = result.reason?.message || 'Some dashboard data could not load';
        }
      });
      return next;
    });

    return stateRef.current;
  }

  /**
   * Targeted refetch of a single resource — used by mutation sites that
   * change something the dashboard cached (e.g. Settings updates a social
   * account's publishLanguage → CreatePost needs to see the new value
   * before the 2-minute TTL expires). Falls back to a no-op if `key` is
   * unknown so callers can fire-and-forget safely.
   */
  async function invalidateResource(key) {
    if (!RESOURCE_TTLS[key]) return stateRef.current;
    try {
      const value = await loadResource(key);
      setState(previous => ({
        ...previous,
        [key]: value,
        fetchedAt: { ...previous.fetchedAt, [key]: Date.now() },
      }));
    } catch (err) {
      // Soft-fail: keep the old cached value rather than blanking the UI.
      // Next ensureDashboardData() call will retry naturally.
      setState(previous => ({ ...previous, error: err?.message || previous.error }));
    }
    return stateRef.current;
  }

  function loadResource(key) {
    if (inFlightRef.current[key]) return inFlightRef.current[key];
    const promise = resourceLoaders[key]()
      .finally(() => {
        if (inFlightRef.current[key] === promise) delete inFlightRef.current[key];
      });
    inFlightRef.current[key] = promise;
    return promise;
  }

  function hasInFlightRequests() {
    return RESOURCE_KEYS.some(key => Boolean(inFlightRef.current[key]));
  }

  useEffect(() => {
    const refreshIfDashboardVisible = () => {
      if (!isDashboardRouteActive()) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      ensureDashboardData();
    };
    window.addEventListener('focus', refreshIfDashboardVisible);
    document.addEventListener('visibilitychange', refreshIfDashboardVisible);
    return () => {
      window.removeEventListener('focus', refreshIfDashboardVisible);
      document.removeEventListener('visibilitychange', refreshIfDashboardVisible);
    };
  }, []);

  return (
    <DashboardDataContext.Provider value={{ ...state, ensureDashboardData, invalidateResource }}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const context = useContext(DashboardDataContext);
  if (!context) throw new Error('useDashboardData must be used inside DashboardDataProvider');
  return context;
}

const resourceLoaders = {
  async publishingPrefs() {
    try {
      const response = await api.profile.getPublishingPreferences();
      return response.publishingPreferences || defaultPublishingPreferences();
    } catch (error) {
      if (error?.status === 404) return defaultPublishingPreferences();
      throw error;
    }
  },
  async entitlements() {
    return api.billing.getEntitlements();
  },
  async schedule() {
    const response = await api.scheduler.list({ limit: 20 });
    return response.posts || [];
  },
  async socialAccounts() {
    const response = await api.socialAccounts.list();
    return response.accounts || [];
  },
};

function staleResourceKeys(state, force) {
  const now = Date.now();
  return RESOURCE_KEYS.filter(key => force || !state.fetchedAt[key] || now - state.fetchedAt[key] >= RESOURCE_TTLS[key]);
}

function emptyFetchedAt() {
  return RESOURCE_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});
}

function defaultPublishingPreferences() {
  return {
    autopilotMode: 'approval',
    postsPerWeek: 3,
  };
}

function isDashboardRouteActive() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  return path === '/' || path === '/dashboard';
}
